// UnscheduledReport.js – sortable & fully responsive

import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  Tabs,
  TabList,
  Tab,
  useTheme,
  useColorMode,
  Link,
  Stack,
  SimpleGrid,
  useBreakpointValue,
} from "@chakra-ui/react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";

function UnscheduledReport() {
  // ---------------------------------------------------------------- State --
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const navigate = useNavigate();

  // -------------------------------------------------------------- Theme ----
  const { colorMode } = useColorMode();
  const theme = useTheme();
  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  // ----------------------------------------------------------- Responsive --
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // ----------------------------------------------------------- Period Tabs --
  const periods = [
    { label: "0‑6 Months", value: 0 },
    { label: "6‑9 Months", value: 1 },
    { label: "9‑12 Months", value: 2 },
    { label: "12‑18 Months", value: 3 },
    { label: "18‑36 Months", value: 4 },
  ];

  // --------------------------------------------- Helpers: transform row ----
  const transform = (item) => {
    const lastCall = item.calls?.length ? item.calls[item.calls.length - 1].created : null;
    const lastTextEmail = item.conversations?.length ? item.conversations[item.conversations.length - 1].created : null;

    // Hygiene due = lastCleaning + 6 months
    const lastCleaning = item.treatments?.[0]?.lastCleaning?.[0]?.completed_date || null;
    const hygieneDue = lastCleaning ? moment(lastCleaning).add(6, "months").toISOString() : moment(item.starttime).toISOString();

    // Determine if hygiene is past‑due (before today)
    const hygienePastDue = hygieneDue ? moment(hygieneDue).isBefore(moment(), "day") : false;
    const hygieneProvider =
      item.treatments?.[0]?.lastCleaning?.[0]?.provider?.[0] &&
      `${item.treatments?.[0]?.lastCleaning?.[0]?.provider?.[0]?.first_name} ${item.treatments?.[0]?.lastCleaning?.[0]?.provider?.[0]?.last_name}`;

    // Outstanding (unscheduled) treatment total (cents)
    const outstanding = item.treatments?.[0]?.outstanding?.reduce((sum, o) => sum + (o.totalAmount || 0), 0) || 0;

    return {
      id: item._id,
      patient: `${item.patient?.first_name ?? ""} ${item.patient?.last_name ?? ""}`.trim(),
      lastVisit: item.starttime,
      provider: `${item.provider_first_name ?? ""} ${item.provider_last_name ?? ""}`.trim(),
      lastCleaning,
      hygieneProvider,
      hygieneDue,
      hygienePastDue,
      lastCall,
      lastTextEmail,
      outstanding, // cents
      // IDs for inbox nav
      userId: item.user?._id,
      bid: item.bid,
      patient_guid: item.patient_guid,
    };
  };

  // Navigate to inbox -------------------------------------------------------
  const goToInbox = (row) => {
    const uid = row.userId || `${row.bid}:${row.patient_guid}`;
    const newSearchParams = new URLSearchParams(window.location.search);
    navigate(`/inbox/${uid}?${newSearchParams.toString()}`);
  };

  // ------------------------------------------------------ Fetch & setRows --
  const fetchData = async (period = 0) => {
    setIsLoading(true);
    try {
      const resp = await cqAPI.get(`/reports/unscheduled?period=${period}`);
      if (resp.type === "ok") {
        setRows(resp.data.map(transform));
      }
    } catch (err) {
      console.error("Error fetching unscheduled patients:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedPeriod);
  }, [selectedPeriod]);

  // ----------------------------------------------------------- Sorting ----
  const sortedRows = useMemo(() => {
    const items = [...rows];
    if (sortConfig.key !== null) {
      items.sort((a, b) => {
        const { key } = sortConfig;
        // String columns
        if (["patient", "provider", "hygieneProvider"].includes(key)) {
          return sortConfig.direction === "ascending" ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]);
        }
        // Numeric columns
        if (key === "outstanding") {
          return sortConfig.direction === "ascending" ? a.outstanding - b.outstanding : b.outstanding - a.outstanding;
        }
        // Boolean column (hygienePastDue)
        if (key === "hygienePastDue") {
          const va = a.hygienePastDue ? 1 : 0;
          const vb = b.hygienePastDue ? 1 : 0;
          return sortConfig.direction === "ascending" ? va - vb : vb - va;
        }
        // Date columns – treat null as earliest
        const va = a[key] ? new Date(a[key]) : new Date(0);
        const vb = b[key] ? new Date(b[key]) : new Date(0);
        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [rows, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    setSortConfig({ key, direction });
  };

  // ----------------------------------------------------------- Column map --
  const columns = [
    { key: "patient", label: "Patient", basis: { base: "160px", lg: "14%" }, click: true },
    { key: "lastVisit", label: "Last Visit", basis: { base: "120px", lg: "10%" } },
    { key: "provider", label: "Provider", basis: { base: "120px", lg: "10%" } },
    { key: "lastCleaning", label: "Last Hygiene", basis: { base: "120px", lg: "10%" } },
    { key: "hygieneProvider", label: "Hygiene Provider", basis: { base: "160px", lg: "12%" } },
    { key: "hygieneDue", label: "Hygiene Due", basis: { base: "120px", lg: "10%" } },
    { key: "hygienePastDue", label: "Past Due", basis: { base: "70px", lg: "6%" }, center: true },
    { key: "lastCall", label: "Last Call", basis: { base: "120px", lg: "10%" }, hideBelow: "lg" },
    { key: "lastTextEmail", label: "Last Text/Email", basis: { base: "150px", lg: "11%" }, hideBelow: "lg" },
    { key: "outstanding", label: "Unscheduled $", basis: { base: "120px", lg: "10%" }, alignRight: true },
  ];

  // ----------------------------------------------------------- Loading ----
  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  // -------------------------------------------------------------- Render --
  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Period selector */}
      <Tabs
        variant="enclosed"
        colorScheme="blue"
        index={selectedPeriod}
        onChange={(i) => setSelectedPeriod(i)}
        mb={4}
        isFitted={isMobile}
        size={isMobile ? "sm" : "md"}
      >
        <TabList>
          {periods.map((p) => (
            <Tab key={p.value}>{p.label}</Tab>
          ))}
        </TabList>
      </Tabs>

      {/* Mobile layout */}
      {isMobile ? (
        <Stack spacing={3}>
          {sortedRows.map((r) => (
            <Box
              key={r.id}
              p={3}
              borderWidth="1px"
              borderRadius="lg"
              _hover={{ bg: hoverBgColor }}
              fontSize="sm"
            >
              <HStack justifyContent="space-between" alignItems="flex-start">
                <Link onClick={() => goToInbox(r)} fontWeight="bold" textDecoration="underline">
                  {r.patient || "—"} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: "4px" }} />
                </Link>
                {r.outstanding ? <Text fontWeight="bold">${(r.outstanding).toFixed(2)}</Text> : null}
              </HStack>
              <SimpleGrid columns={2} spacingY={1} mt={2}>
                <Text color="gray.500">Last Visit</Text>
                <Text>{r.lastVisit ? moment(r.lastVisit).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Provider</Text>
                <Text>{r.provider || "—"}</Text>
                <Text color="gray.500">Last Hygiene</Text>
                <Text>{r.lastCleaning ? moment(r.lastCleaning).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Hyg. Provider</Text>
                <Text>{r.hygieneProvider || "—"}</Text>
                <Text color="gray.500">Hygiene Due</Text>
                <Text>{r.hygieneDue ? moment(r.hygieneDue).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Past Due</Text>
                <Box>{r.hygienePastDue && <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.red[500] }} />}</Box>
                <Text color="gray.500">Last Call</Text>
                <Text>{r.lastCall ? moment(r.lastCall).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Last Text/Email</Text>
                <Text>{r.lastTextEmail ? moment(r.lastTextEmail).format("MMM D YYYY") : "—"}</Text>
              </SimpleGrid>
            </Box>
          ))}
        </Stack>
      ) : (
        /* Desktop/tablet layout */
        <VStack spacing={0} align="stretch" minW="1100px">
          {/* Header */}
          <Flex borderBottom="1px" borderColor="gray.200" p={2} bg={hoverBgColor} fontWeight="bold" fontSize="sm">
            <HStack spacing={4} flex="1">
              {columns.map((c) => (
                <Text
                  key={c.key}
                  flexBasis={c.basis}
                  cursor="pointer"
                  textAlign={c.center ? "center" : c.alignRight ? "right" : "left"}
                  display={c.hideBelow ? { base: "none", [c.hideBelow]: "block" } : "block"}
                  onClick={() => requestSort(c.key)}
                >
                  {c.label}
                </Text>
              ))}
            </HStack>
          </Flex>

          {/* Rows */}
          {sortedRows.map((r) => (
            <Flex key={r.id} p={2} _hover={{ bg: hoverBgColor }} fontSize="sm">
              <HStack spacing={4} flex="1" alignItems="center">
                {/* Patient column with link */}
                <Box flexBasis={columns[0].basis}>
                  <Link onClick={() => goToInbox(r)} textDecoration="underline">
                    {r.patient || "—"} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: "4px" }} />
                  </Link>
                </Box>

                {/* Other columns */}
                <Text flexBasis={columns[1].basis}>{r.lastVisit ? moment(r.lastVisit).format("MMM D YYYY") : "—"}</Text>
                <Text flexBasis={columns[2].basis}>{r.provider || "—"}</Text>
                <Text flexBasis={columns[3].basis}>{r.lastCleaning ? moment(r.lastCleaning).format("MMM D YYYY") : "—"}</Text>
                <Text flexBasis={columns[4].basis}>{r.hygieneProvider || "—"}</Text>
                <Text flexBasis={columns[5].basis}>{r.hygieneDue ? moment(r.hygieneDue).format("MMM D YYYY") : "—"}</Text>
                <Box flexBasis={columns[6].basis} textAlign="center">
                  {r.hygienePastDue && <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.red[500] }} />}
                </Box>
                <Text display={{ base: "none", lg: "block" }} flexBasis={columns[7].basis}>
                  {r.lastCall ? moment(r.lastCall).format("MMM D YYYY") : "—"}
                </Text>
                <Text display={{ base: "none", lg: "block" }} flexBasis={columns[8].basis}>
                  {r.lastTextEmail ? moment(r.lastTextEmail).format("MMM D YYYY") : "—"}
                </Text>
                <Text flexBasis={columns[9].basis} textAlign="right">
                  {r.outstanding ? `$${(r.outstanding).toFixed(2)}` : "—"}
                </Text>
              </HStack>
            </Flex>
          ))}
        </VStack>
      )}

      {/* Total */}
      <Box textAlign="right" mt={2}>
        <Text fontWeight="bold">Total Patients: {rows.length}</Text>
      </Box>
    </Box>
  );
}

export default UnscheduledReport;
